import { useEffect, useState, useCallback } from "react";
import { FiPlus, FiX, FiAlertOctagon, FiUser, FiCalendar, FiFilter } from "react-icons/fi";
import { MdWarning } from "react-icons/md";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "react-toastify";
import { api } from "../../services/api";
import { PageTitle } from "../../components/PageTitle";
import { EmptyState } from "../../components/EmptyState";
import { ModalEmergencyObservation } from "../../components/ModalEmergencyObservation";
import { ModalCloseObservation } from "../../components/ModalCloseObservation";
import { MultiSelectCustomerSearch } from "../../components/MultiSelectCustomerSearch";

interface User {
  id: string;
  name: string;
}

interface Customer {
  id: string;
  name: string;
}

interface Observation {
  id: string;
  costumer_id: string;
  costumer: Customer;
  user_id: string;
  user: User;
  text: string;
  status: string;
  opened_at: string;
  created_at: string;
}

type DateSort = "recent" | "oldest";
type CustomerSort = "asc" | "desc";

export function EmergencyObservations() {
  const [observations, setObservations] = useState<Observation[]>([]);
  const [filteredCustomerIds, setFilteredCustomerIds] = useState<string[]>([]);
  const [dateSort, setDateSort] = useState<DateSort>("recent");
  const [customerSort, setCustomerSort] = useState<CustomerSort>("asc");
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const [selectedObservation, setSelectedObservation] = useState<Observation | null>(null);
  const [isClosing, setIsClosing] = useState(false);

  // Scroll infinito
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const itemsPerPage = 20;

  const loadObservations = useCallback(async (resetPage = false) => {
    setLoading(true);
    try {
      const params: any = {};

      if (filteredCustomerIds.length > 0) {
        params.costumer_ids = filteredCustomerIds;
      }

      const response = await api.get("/costumer-observations/open/all", { params });

      const data: Observation[] = response.data;

      // Ordenar por data
      let sorted = [...data];
      if (dateSort === "recent") {
        sorted.sort((a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
      } else {
        sorted.sort((a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
      }

      // Ordenar por cliente (secundário)
      if (customerSort === "asc") {
        sorted.sort((a, b) => {
          const dateA = new Date(dateSort === "recent" ? b.created_at : a.created_at).getTime();
          const dateB = new Date(dateSort === "recent" ? a.created_at : b.created_at).getTime();
          if (dateA === dateB) {
            return a.costumer.name.localeCompare(b.costumer.name);
          }
          return 0;
        });
      } else {
        sorted.sort((a, b) => {
          const dateA = new Date(dateSort === "recent" ? b.created_at : a.created_at).getTime();
          const dateB = new Date(dateSort === "recent" ? a.created_at : b.created_at).getTime();
          if (dateA === dateB) {
            return b.costumer.name.localeCompare(a.costumer.name);
          }
          return 0;
        });
      }

      setObservations(sorted);
      setHasMore(sorted.length > itemsPerPage);
      if (resetPage) {
        setPage(1);
      }
    } catch (error) {
      toast.error("Erro ao carregar observações");
    } finally {
      setLoading(false);
    }
  }, [filteredCustomerIds, dateSort, customerSort]);

  useEffect(() => {
    loadObservations(true);
  }, [loadObservations]);

  const handleCloseObservation = async () => {
    if (!selectedObservation) return;

    setIsClosing(true);
    try {
      await api.patch("/costumer-observations/close", {
        observation_ids: [selectedObservation.id],
        costumer_id: selectedObservation.costumer_id,
      });

      // Remover da lista
      setObservations(prev => prev.filter(obs => obs.id !== selectedObservation.id));

      toast.success("Observação fechada com sucesso!");
      setCloseModalOpen(false);
      setSelectedObservation(null);
    } catch (error) {
      toast.error("Erro ao fechar observação");
    } finally {
      setIsClosing(false);
    }
  };

  const displayedObservations = observations.slice(0, page * itemsPerPage);

  const handleLoadMore = () => {
    if (displayedObservations.length < observations.length) {
      setPage(prev => prev + 1);
    }
  };

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <PageTitle title="Observações Emergenciais" backButton backPath="/home" />

          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-lg shadow-red-500/30 hover:shadow-xl hover:scale-105"
          >
            <FiPlus size={20} />
            <span className="font-semibold">Nova Observação</span>
          </button>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 mb-6">
          {/* Header do card de filtros */}
          <div className="bg-gradient-to-r from-[#0085BD] to-[#006a96] px-6 py-4 rounded-t-2xl">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <FiFilter className="h-5 w-5" />
              Filtros
            </h3>
          </div>

          <div className="p-6">
            {/* Filtro de Clientes */}
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Cliente(s)
              </label>
              <MultiSelectCustomerSearch
                value={filteredCustomerIds}
                onChange={setFilteredCustomerIds}
                placeholder="Selecione clientes..."
                endpoint="/costumer-observations/customers/with-open"
              />
            </div>

            {/* Limpar filtros */}
            {filteredCustomerIds.length > 0 && (
              <div className="mb-4 flex items-center gap-2">
                <span className="text-sm text-gray-600">
                  {filteredCustomerIds.length} cliente(s) selecionado(s)
                </span>
                <button
                  onClick={() => setFilteredCustomerIds([])}
                  className="text-sm text-red-600 hover:text-red-700 font-medium flex items-center gap-1"
                >
                  <FiX size={16} />
                  Limpar filtros
                </button>
              </div>
            )}

            {/* Ordenação */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200">
              {/* Ordenação por Data */}
              <div className="space-y-2">
                <span className="text-sm font-semibold text-gray-700">
                  Ordenar por Data
                </span>
                <select
                  value={dateSort}
                  onChange={(e) => setDateSort(e.target.value as DateSort)}
                  className="h-[52px] px-4 w-full rounded-lg text-gray-700 bg-white border border-gray-200 hover:border-[#0085BD] focus:border-[#0085BD] focus:ring-1 focus:ring-[#0085BD] transition-colors"
                >
                  <option value="recent">Mais recente</option>
                  <option value="oldest">Mais antiga</option>
                </select>
              </div>

              {/* Ordenação por Cliente */}
              <div className="space-y-2">
                <span className="text-sm font-semibold text-gray-700">
                  Ordenar por Cliente
                </span>
                <select
                  value={customerSort}
                  onChange={(e) => setCustomerSort(e.target.value as CustomerSort)}
                  className="h-[52px] px-4 w-full rounded-lg text-gray-700 bg-white border border-gray-200 hover:border-[#0085BD] focus:border-[#0085BD] focus:ring-1 focus:ring-[#0085BD] transition-colors"
                >
                  <option value="asc">A-Z</option>
                  <option value="desc">Z-A</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Listagem */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin h-12 w-12 border-4 border-red-500 border-t-transparent rounded-full" />
          </div>
        ) : observations.length === 0 ? (
          <EmptyState
            icon={FiAlertOctagon}
            title="Nenhuma observação emergencial"
            description={
              filteredCustomerIds.length > 0
                ? "Não há observações abertas para os clientes selecionados"
                : "Não há observações emergenciais abertas no momento"
            }
          />
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {displayedObservations.map((observation) => (
                <div
                  key={observation.id}
                  className="bg-white rounded-xl shadow-md border border-gray-100 hover:shadow-lg transition-all duration-200 overflow-hidden"
                >
                  {/* Header do Card */}
                  <div className="bg-gradient-to-r from-[#0085BD] to-[#006a96] px-4 py-3 flex items-center gap-2">
                    <MdWarning size={20} className="text-white" />
                    <h3 className="text-white font-bold truncate">
                      {observation.costumer.name}
                    </h3>
                  </div>

                  {/* Conteúdo */}
                  <div className="p-4 space-y-3">
                    {/* Texto da observação */}
                    <div className="bg-red-50 p-3 rounded-lg border border-red-100">
                      <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">
                        {observation.text}
                      </p>
                    </div>

                    {/* Informações */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <FiUser size={14} className="text-gray-400" />
                        <span>Criado por: <strong>{observation.user.name}</strong></span>
                      </div>

                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <FiCalendar size={14} className="text-gray-400" />
                        <span>
                          Aberto em: <strong>
                            {format(new Date(observation.opened_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                          </strong>
                        </span>
                      </div>
                    </div>

                    {/* Botão de fechar */}
                    <button
                      onClick={() => {
                        setSelectedObservation(observation);
                        setCloseModalOpen(true);
                      }}
                      className="w-full mt-4 px-4 py-2.5 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-semibold flex items-center justify-center gap-2"
                    >
                      <FiX size={18} />
                      Fechar Observação
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Botão Carregar Mais */}
            {displayedObservations.length < observations.length && (
              <div className="flex justify-center mt-8">
                <button
                  onClick={handleLoadMore}
                  className="px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all font-semibold"
                >
                  Carregar mais
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal Nova Observação */}
      <ModalEmergencyObservation
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={() => loadObservations(true)}
      />

      {/* Modal Fechar Observação */}
      <ModalCloseObservation
        isOpen={closeModalOpen}
        onClose={() => {
          setCloseModalOpen(false);
          setSelectedObservation(null);
        }}
        onConfirm={handleCloseObservation}
        isLoading={isClosing}
        observationText={selectedObservation?.text}
      />
    </div>
  );
}
